import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useNavigate } from 'react-router';
import { ChangeEvent, SubmitEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { type InventoryItem, validate as validateInventoryItem } from '../../models/InventoryItem';
import { Autocomplete, Badge, styled, Tooltip } from '@mui/material';
import { UnitType, unitTypeToUnit, unitTypeToUnits, type Item } from '../../models/Item';
import NumberField from '../NumberField';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export interface InventoryItemFormState {
  values: Partial<Omit<InventoryItem, 'id'>>;
  errors: Partial<Record<keyof InventoryItemFormState['values'], string>>;
}

export interface InventoryItemFormProps {
  formState?: InventoryItemFormState;
  items: Item[];
  initialValue?: InventoryItem;
  initialErrors?: Partial<Record<keyof InventoryItemFormState['values'], string>>;
  onFieldChange?: (
    name: keyof InventoryItemFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit?: (formValues: Partial<InventoryItemFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<InventoryItemFormState['values']>) => void;
  onFormValuesUpdate?: (formValues: Partial<InventoryItemFormState['values']>) => void;
  submitButtonLabel?: string;
  backButtonPath?: string;
  showActionButtons?: boolean;
  isFromNewPurchaseOrder?: boolean;
}

export const newInventoryItem = {
  id: 0,
  quantityOnHand: 0,
  quantityOrdered: 0,
  isArchived: false,
  costPerUnit: 0,
  itemId: 0,
  vendorId: 0,
  purchaseOrderId: 0,
  outOfStock: false,
  lotNumber: '',
  orderDetails: ''
} as InventoryItem;

export default function InventoryItemForm(props: InventoryItemFormProps) {
  const {
    formState,
    items,
    initialValue,
    initialErrors = {},
    onFieldChange = () => { },
    onSubmit = () => { },
    onReset = () => { },
    onFormValuesUpdate = () => { },
    submitButtonLabel = 'Create',
    backButtonPath,
    showActionButtons = true,
    isFromNewPurchaseOrder = true
  } = props;

  const formValues = formState?.values;
  const formErrors = formState?.errors;

  const [errors, setErrors] = useState<Partial<Record<keyof InventoryItemFormState['values'], string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleFormFieldChange = useCallback(
    (name: keyof InventoryItemFormState['values'], value: FormFieldValue) => {
      const validateField = (values: Partial<InventoryItemFormState['values']>) => {
        const { issues } = validateInventoryItem(values);

        const issue = issues?.find((issue) => issue.path?.[0] === name);

        if (issue) {
          setErrors({
            ...errors,
            [name]: issue.message,
          });
          return false;
        }

        setErrors({});

        return true;
      };

      const newFormValues = { ...initialValue, [name]: value };

      onFieldChange(name, value);

      const valid = validateField(newFormValues);

      if (valid) {
        onFormValuesUpdate(newFormValues);
      }
    },
    [initialValue, errors, onFormValuesUpdate, onFieldChange],
  );

  const handleNumberFieldChange = useCallback(
    (value: number | null, fieldName: string) => {
      handleFormFieldChange(
        fieldName as keyof InventoryItemFormState['values'],
        value,
      );
    },
    [handleFormFieldChange],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      handleFormFieldChange(
        event.target.name as keyof InventoryItemFormState['values'],
        event.target.value,
      );
    },
    [handleFormFieldChange],
  );

  const handleFileFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {

      handleFormFieldChange(
        event.target.name as keyof InventoryItemFormState['values'],
        event.target.files,
      );
    },
    [handleFormFieldChange],
  );

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      if (formValues) {
        event.preventDefault();

        setIsSubmitting(true);
        try {
          await onSubmit(formValues);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [formValues, onSubmit],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(initialValue || newInventoryItem);
    }
  }, [initialValue, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/inventory-items');
  }, [navigate, backButtonPath]);

  const getUnits = useCallback((selectedItemId: number | undefined) => {
    if ((selectedItemId || 0) > 0) {
      const selectedItem = items.find(item => +item.id === selectedItemId);

      if (selectedItem) {
        return unitTypeToUnits(selectedItem.unitType as UnitType);
      }
    }

    return '';
  }, [items]);

  const getCostUnits = useCallback((selectedItemId: number | undefined) => {
    if ((selectedItemId || 0) > 0) {
      const selectedItem = items.find(item => +item.id === selectedItemId);

      if (selectedItem) {
        return `/${unitTypeToUnit(selectedItem.unitType as UnitType)}`;
      }
    }

    return '';
  }, [items]);

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onReset={handleReset}
      onSubmit={handleSubmit}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          {isFromNewPurchaseOrder ?
            <Grid size={{ xs: 6, sm: 3 }}>
              <Autocomplete
                getOptionLabel={(option) => option.name}
                options={items || []}
                value={initialValue?.item || formValues?.item}
                onChange={(_, newValue: Item | null) => {
                  handleFormFieldChange(
                    'itemId',
                    +(newValue?.id || 0)
                  );
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Item"
                    error={!!errors.itemId || !!initialErrors.itemId || !!formErrors?.itemId}
                    helperText={(errors.itemId || initialErrors.itemId || !!formErrors?.itemId) ?? ' '}
                    slotProps={{
                      inputLabel: {
                        shrink: true
                      }
                    }}
                  />
                )}
                renderOption={(props, item) => {
                  const { key, ...optionProps } = props;
                  return (
                    <Box
                      key={key}
                      component="li"
                      {...optionProps}
                    >
                      {item.name}
                    </Box>
                  );
                }}
              />
            </Grid> : <></>
          }
          <Grid size={{ xs: 4, sm: 2 }}>
            {isFromNewPurchaseOrder ?
              <NumberField
                value={initialValue?.quantityOrdered || formValues?.quantityOrdered || null}
                onValueChange={(event) => handleNumberFieldChange(event, 'quantityOrdered')}
                name="quantityOrdered"
                label="Ordered Qty"
                error={!!errors.quantityOrdered || !!initialErrors.quantityOrdered || !!formErrors?.quantityOrdered}
                helperText={(errors.quantityOrdered || initialErrors.quantityOrdered || formErrors?.quantityOrdered) ?? ' '}
                weightFieldUnits={getUnits(initialValue?.itemId || formValues?.itemId)}
                required
                fullWidth
              /> :
              <NumberField
                value={initialValue?.quantityOnHand || formValues?.quantityOnHand || null}
                onValueChange={(event) => handleNumberFieldChange(event, 'quantityOnHand')}
                name="quantityOnHand"
                label="Qty on Hand"
                weightFieldUnits={getUnits(initialValue?.itemId || formValues?.itemId)}
                fullWidth
              />
            }
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <TextField
              value={(initialValue?.lotNumber || formValues?.lotNumber) ?? ''}
              onChange={handleTextFieldChange}
              name="lotNumber"
              label="Lot Number"
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              value={(initialValue?.orderDetails || formValues?.orderDetails) ?? ''}
              onChange={handleTextFieldChange}
              name="orderDetails"
              label="Order Details"
              helperText={' '}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 4, sm: 2 }}>
            <NumberField
              value={initialValue?.costPerUnit || formValues?.costPerUnit || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'costPerUnit')}
              name="costPerUnit"
              label="Cost per Unit"
              error={!!errors.costPerUnit || !!initialErrors.costPerUnit || !!formErrors?.costPerUnit}
              helperText={(errors.costPerUnit || initialErrors.costPerUnit || formErrors?.costPerUnit) ?? ' '}
              weightFieldUnits={getCostUnits(initialValue?.itemId || formValues?.itemId)}
              required
              fullWidth
              currencyField
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <NumberField
              value={initialValue?.surchargePerUnit || formValues?.surchargePerUnit || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'surchargePerUnit')}
              name="surchargePerUnit"
              label="Surcharge per Unit"
              weightFieldUnits={getCostUnits(initialValue?.itemId || formValues?.itemId)}
              fullWidth
              currencyField
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <NumberField
              value={initialValue?.shippingCostPerUnit || formValues?.shippingCostPerUnit || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'shippingCostPerUnit')}
              name="shippingCostPerUnit"
              label="Shipping Cost per Unit"
              weightFieldUnits={getCostUnits(initialValue?.itemId || formValues?.itemId)}
              fullWidth
              currencyField
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <NumberField
              value={initialValue?.surchargeOrDiscount || formValues?.surchargeOrDiscount || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'surchargeOrDiscount')}
              name="surchargeOrDiscount"
              label="Surcharge/Discount"
              fullWidth
              currencyField
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
              >
                Attachments
                <VisuallyHiddenInput
                  type="file"
                  name="updatedAttachments"
                  onChange={(event) => handleFileFieldChange(event)}
                  multiple
                />
              </Button>
              <Tooltip title={
                initialValue?.updatedAttachments ?
                  Array.from(initialValue.updatedAttachments).map((file, index) => (
                    <div key={index + 1}>
                      {file.name}
                    </div>
                  )) : null}>
                <Badge
                  badgeContent={initialValue?.updatedAttachments?.length || '0'}
                  color="primary">
                  <AttachFileIcon color="action" />
                </Badge>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </FormGroup>
      {showActionButtons ? (
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="success"
            startIcon={<SaveIcon />}
            loading={isSubmitting}
          >
            {submitButtonLabel}
          </Button>
        </Stack>
      ) : <></>}
    </Box>
  );
}
